import { PaletteMode } from "@mui/material"
import { ReactImageGalleryItem } from "react-image-gallery"
import contentfulClient from "../Utils/ContentfulClient"

export interface ImageGalleryItemAsset {
  id: string
  url: string
  size: number
  width: number
  height: number
}

export class ImageGalleryItem implements ReactImageGalleryItem {
  id: string = ""
  headerMode: PaletteMode = "dark"
  photoTitle: string = ""
  photoSubtitle?: string
  photoTitleColor: string = "white"
  desktopPhoto: ImageGalleryItemAsset = {
    url: "",
    id: "",
    size: 0,
    width: 0,
    height: 0
  }
  mobilePhoto: ImageGalleryItemAsset = {
    url: "",
    id: "",
    size: 0,
    width: 0,
    height: 0
  }
  desktopPhotoWebp?: ImageGalleryItemAsset
  mobilePhotoWebp?: ImageGalleryItemAsset

  public set original(value: string) {
    // do nothing
  }

  public get original() {
    return this.desktopPhoto.url
  }
}

export interface ImageGalleryChannel {
  channelTitle: string
  channelDescription: string
  items: ImageGalleryItem[]
}

export type ImageGallery = {
  expiration: number
  [key: string]: ImageGalleryChannel|number
}

class HomeImageGalleryApi {

  cacheLifetime: number // seconds
  cacheKey: string

  constructor(cacheLifetime?: number) {
    this.cacheLifetime = cacheLifetime ? cacheLifetime * 1000 : 0 // do not cache by default
    this.cacheKey = "jg.homeImageGallery"
  }

  /**
   * Update the cache lifetime for future checks
   * @param value the number of seconds to set the lifetime to
   */
  public updateCacheLifetime(value: number) {
    this.cacheLifetime = value * 1000
  }

  public async getImageGallery(): Promise<ImageGallery> {
    const rawCache = localStorage.getItem(this.cacheKey)
    const now = new Date().getTime()
    let cache: ImageGallery = rawCache ? JSON.parse(rawCache) : {
      expiration: now
      // By default, empty.  Should display a black bg.
    }
    if (cache.expiration <= new Date().getTime()) {
      try {
        // console.log(`image gallery cache ${!cache ? "is missing" : "expired " + (now - cache.expiration) + " ms ago"}.  retrieving.`)
        const entries = await this.retrieveImageGallery()
        cache = this.parseContentfulImageGallery(entries)
        localStorage.setItem(this.cacheKey, JSON.stringify(cache))
        // console.log("Retrieved image gallery:", Object.keys(cache))
      } catch (err) {
        console.error("error retrieving and parsing image gallery", err)
      }
    } else {
      // console.log("returning cached image gallery.  ttl is", cache.expiration - now, "ms");
    }
    return cache
  }

  public async retrieveImageGallery() {
    try {
      const entries = await contentfulClient.getEntries({
        content_type: "channelBackgroundList",
        include: 2
      })
      return entries
    } catch (err) {
      console.error("Unable to refresh image gallery from network", err)
    }
  }

  private parseContentfulImageGallery(entries: any): ImageGallery {
    const result: ImageGallery = {
      expiration: new Date().getTime() + this.cacheLifetime,
    }
    for (const entry of entries.items) {
      const imageGalleryChannel: ImageGalleryChannel = {
        channelTitle: entry.fields.channelTitle,
        channelDescription: entry.fields.channelDescription,
        items: [],
      }
      result[entry.fields.channelId] = imageGalleryChannel
      result.channelTitle = entry.fields.channelTitle
      result.channelDescription = entry.fields.channelDescription

      for (const carouselImage of entry.fields.carouselImages) {
       
        if (carouselImage.sys.type !== "Entry" || !carouselImage.fields) {
          console.log("Not an entry or no fields property.  Skipping")
          continue
        }
        const item: ImageGalleryItem = new ImageGalleryItem()
        item.id = carouselImage.sys.id
        item.headerMode = carouselImage.fields.headerMode
        item.photoTitle = carouselImage.fields.photoTitle
        item.photoSubtitle = carouselImage.fields.photoSubtitle || ""
        item.photoTitleColor = carouselImage.fields.photoTitleColor
        item.desktopPhoto = this.parsePhotoAsset(carouselImage.fields.desktopPhoto)!
        item.mobilePhoto = this.parsePhotoAsset(carouselImage.fields.mobilePhoto)!
        item.desktopPhotoWebp = this.parsePhotoAsset(carouselImage.fields.desktopPhotoWeb)
        item.mobilePhotoWebp = this.parsePhotoAsset(carouselImage.fields.mobilePhotoWebp)
        imageGalleryChannel.items.push(item)
      }
    }

    return result
  }

  private parsePhotoAsset(baseObject: any): ImageGalleryItemAsset|undefined {
    if (!baseObject) return undefined

    return {
      id: baseObject.sys.id,
      url: baseObject.fields.file.url,
      size: baseObject.fields.file.details.size,
      width: baseObject.fields.file.details.image.width,
      height: baseObject.fields.file.details.image.height
    }
  }
}

const galleryApi = new HomeImageGalleryApi(600)
export default galleryApi
